import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, X } from '@/design-system/icons';
import { Link, useLocation } from '@tanstack/react-router';
import { SidebarProps, NavItem } from './types';

function NavItemComponent({
  item,
  isActive,
  level = 0,
}: {
  item: NavItem;
  isActive: boolean;
  level?: number;
}) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  if (item.isHeadline) {
    return (
      <li className="pt-5 px-5 lg:px-8 mt-5 border-t border-border first:border-transparent first:pt-0">
        <span className="block text-xs uppercase text-onsurface opacity-50">
          {item.label ?? t(item.labelKey)}
        </span>
      </li>
    );
  }

  if (hasChildren) {
    const accordionId = `${item.id}-accordion`;
    const accordionSubId = `${item.id}-accordion-sub`;

    return (
      <li className="hs-accordion px-2 lg:px-5 text-onsurface" id={accordionId}>
        <button
          type="button"
          className={`hs-accordion-toggle w-full text-start flex gap-x-3 py-2 px-3 text-sm rounded-lg hover:bg-surface-darker disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus:bg-surface-darker text-primary`}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-controls={accordionSubId}
        >
          {level === 0 && item.icon && (
            <span className="shrink-0 mt-0.5 size-4">{item.icon}</span>
          )}
          {item.label ?? t(item.labelKey)}
          <ChevronDown
            className={`shrink-0 mt-1 size-3.5 ms-auto transition ${isExpanded ? '-rotate-180' : ''
              }`}
          />
        </button>

        <div
          id={accordionSubId}
          className={`w-full overflow-hidden transition-[height] duration-300 ${isExpanded ? '' : 'hidden'
            }`}
          role="region"
          aria-labelledby={accordionId}
        >
          <ul className="ps-7 mt-1 flex flex-col gap-y-1 relative before:absolute before:top-0 before:start-4.5 before:w-0.5 before:h-full before:bg-surface-darker">
            {item.children?.map((child) => (
              <NavItemComponent
                key={child.id}
                item={child}
                isActive={false}
                level={level + 1}
              />
            ))}
          </ul>
        </div>
      </li>
    );
  }

  return (
    <li className={level === 0 ? 'px-2 lg:px-5' : ''}>
      {item.href && (
        <Link
          to={item.href}
          search={item.query}
          className={`flex gap-x-3 py-2 px-3 text-sm rounded-lg hover:bg-surface-darker focus:outline-none focus:bg-surface-darker ${isActive ? 'bg-surface-darker text-onsurface' : 'text-onsurface'
            }`}
        >
          {level === 0 && item.icon && (
            <span className="shrink-0 mt-0.5 size-4">{item.icon}</span>
          )}
          {item.label ?? t(item.labelKey)}
        </Link>)}
    </li>
  );
}

export function Sidebar({ items, logo, logoHref = '/', className = '', isOpen = false, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();

  const isItemActive = (item: NavItem): boolean => {
    if (item.href && location.pathname === item.href) {
      return true;
    }
    if (item.children) {
      return item.children.some((child) => isItemActive(child));
    }
    return false;
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={`
          transition-transform duration-300 transform
          w-64 h-full
          fixed inset-y-0 start-0 z-60
          bg-surface border-e border-border
          lg:translate-x-0
          overflow-hidden
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${className}`}
        tabIndex={-1}
        aria-label={t('sidebar.navigation')}
      >
        <div className="relative flex flex-col h-full max-h-full pt-3">
          <header className="relative h-12 ps-5 pe-2 lg:ps-8 flex items-center gap-x-1 w-full">
            <Link
              to={logoHref}
              className="flex-none rounded-md w-[60%] text-xl inline-block font-semibold focus:outline-none focus:opacity-80"
              aria-label={t('app.name')}
            >
              {logo ? (
                <div className="">{logo}</div>
              ) : (
                <span className="text-xl font-bold text-onbackground">
                  {t('app.name')}
                </span>
              )}
            </Link>

            <div className="relative lg:hidden ms-auto pr-3">
              <button
                type="button"
                className="size-7 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-m border border-border bg-surface text-onsurface hover:bg-surface-darker focus:outline-none focus:bg-surface-darker disabled:opacity-50 disabled:pointer-events-none"
                onClick={onClose}
                aria-label={t('sidebar.close')}
              >
                <X className="size-4" />
              </button>
            </div>
          </header>

          <div className="mt-6 h-full overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb">
            <nav
              className="hs-accordion-group pb-3 w-full flex flex-col flex-wrap"
              data-hs-accordion-always-open
            >
              <ul className="flex flex-col gap-y-1">
                {items.map((item) => (
                  <NavItemComponent
                    key={item.id}
                    item={item}
                    isActive={isItemActive(item)}
                  />
                ))}
              </ul>
            </nav>
          </div>
        </div>
      </aside>
    </>
  );
}
